
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserAlaresWatchlistNotification from 'Models/Security/Acl/UserAlaresWatchlistNotification';
import VisitorsAlaresWatchlistNotification from 'Models/Security/Acl/VisitorsAlaresWatchlistNotification';
import UserAlaresWebhookAlert from 'Models/Security/Acl/UserAlaresWebhookAlert';
import VisitorsAlaresWebhookAlert from 'Models/Security/Acl/VisitorsAlaresWebhookAlert';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IAlaresWebhookAlertEntityAttributes extends IModelAttributes {
	watchlistEntityId: string;
	referenceId: string;
	status: Enums.processingStatus;
	content: string;


}

@entity('AlaresWebhookAlertEntity', 'Alares Webhook Alert')
export default class AlaresWebhookAlertEntity extends Model
	implements IAlaresWebhookAlertEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserAlaresWatchlistNotification(),
		new VisitorsAlaresWatchlistNotification(),
		new UserAlaresWebhookAlert(),
		new VisitorsAlaresWebhookAlert(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AlaresWebhookAlertEntity, string>()
	@CRUD({
		name: 'Watchlist Entity Id',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public watchlistEntityId: string;

	@observable
	@attribute<AlaresWebhookAlertEntity, string>()
	@CRUD({
		name: 'Reference Id',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public referenceId: string;

	@observable
	@attribute<AlaresWebhookAlertEntity, Enums.processingStatus>()
	@CRUD({
		name: 'Status',
		displayType: 'enum-combobox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.processingStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.processingStatusOptions),
		displayFunction: (attr: Enums.processingStatus) => Enums.processingStatusOptions[attr],
	})
	public status: Enums.processingStatus;
	

	
	/**
	 * Alares report in json
	 */
	@observable
	@attribute<AlaresWebhookAlertEntity, string>()
	@CRUD({
		name: 'Content',
		displayType: 'textarea',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public content: string;


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAlaresWebhookAlertEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAlaresWebhookAlertEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.watchlistEntityId !== undefined) {
				this.watchlistEntityId = attributes.watchlistEntityId;
			}
			if (attributes.referenceId !== undefined) {
				this.referenceId = attributes.referenceId;
			}
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.content !== undefined) {
				this.content = attributes.content;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AlaresWebhookAlertEntity.prototype, 'created');
CRUD(modifiedAttr)(AlaresWebhookAlertEntity.prototype, 'modified');
